import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { Field, FieldGroup, FieldLabel } from '../../../components-v2/Field';
import { Input } from '../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import type { LinkValues } from './linkEditorUtils';
import { styleSheet } from './styles';

export interface ILinkChooserProps {
	isOpen: boolean;
	initialValues: LinkValues;
	onSave: (values: LinkValues) => void;
	onCancel: () => void;
}

const TARGET_OPTION_CURRENT = 'current';
const TARGET_OPTION_NEW = 'new';

const TARGET_OPTIONS = [
	{ label: 'Current window', value: TARGET_OPTION_CURRENT },
	{ label: 'New window', value: TARGET_OPTION_NEW },
];

const targetToOption = (target: string): string => (target === '_blank' ? TARGET_OPTION_NEW : TARGET_OPTION_CURRENT);
const optionToTarget = (option: string): string => (option === TARGET_OPTION_NEW ? '_blank' : '');

export const LinkChooser: React.FC<ILinkChooserProps> = ({ isOpen, initialValues, onSave, onCancel }) => {
	const [url, setUrl] = React.useState(initialValues.url);
	const [text, setText] = React.useState(initialValues.text);
	const [title, setTitle] = React.useState(initialValues.title);
	const [targetOption, setTargetOption] = React.useState(targetToOption(initialValues.target));

	// Seed only on the closed→open transition; re-seeding on every initialValues change would wipe
	// the user's edits if the open command fires again while the dialog is open (Ctrl+K / pencil).
	const wasOpenRef = React.useRef(false);
	React.useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			setUrl(initialValues.url);
			setText(initialValues.text);
			setTitle(initialValues.title);
			setTargetOption(targetToOption(initialValues.target));
		}
		wasOpenRef.current = isOpen;
	}, [isOpen, initialValues]);

	const canSave = url.trim().length > 0;

	// Mirror the URL into the display text as the user types, until the text has its own value
	// (matches the legacy dialog). `url` here is the pre-change value, so once the user edits the
	// text to something other than the URL they entered, the two decouple.
	const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextUrl = e.target.value;
		setText(current => (current === '' || current === url ? nextUrl : current));
		setUrl(nextUrl);
	};

	const handleSave = () => {
		if (!canSave) {
			return;
		}
		onSave({ url: url.trim(), text, title, target: optionToTarget(targetOption) });
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			onCancel();
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange} dismissOnOutsideClick={false}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader>
					<DialogTitle>Insert/Edit Link</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<FieldGroup>
						<Field>
							<FieldLabel htmlFor='link-url'>URL</FieldLabel>
							<Input id='link-url' value={url} onChange={handleUrlChange} autoFocus />
						</Field>
						<Field>
							<FieldLabel htmlFor='link-text'>Text to display</FieldLabel>
							<Input id='link-text' value={text} onChange={e => setText(e.target.value)} />
						</Field>
						<Field>
							<FieldLabel htmlFor='link-title'>Title</FieldLabel>
							<Input id='link-title' value={title} onChange={e => setTitle(e.target.value)} />
						</Field>
						<Field>
							<FieldLabel htmlFor='link-target'>Open link in…</FieldLabel>
							<Select items={TARGET_OPTIONS} value={targetOption} onValueChange={setTargetOption}>
								<SelectTrigger id='link-target' styles={styleSheet.targetTrigger}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{TARGET_OPTIONS.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					</FieldGroup>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={onCancel}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave} disabled={!canSave}>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
